'use client';
import { PlanTypes } from 'ui/utils/constants/maverick';
import { JOIN_FLOW_STEPS, TRIAL } from '@/lib/constants/maverick';
import {
  getMembershipInCart,
  hasProductTypeInCartItemGroups,
} from '../cart.utils';
import { getIsTypeFromAttributes } from '@/lib/utils/attributeHelper';
import type { CartButtonVariants } from './CartCTAButtons';
import { useGetCurrentPageMaverick } from '@/hooks/useGetCurrentPageMaverick';
import { useSiteStore } from '@/store/SiteStoreProvider';
import { useShallow } from 'zustand/react/shallow';
import {
  useSharedButtonLogic,
  type UseSharedButtonLogicReturn,
} from './useSharedButtonLogic';
import { ProductType } from '@/lib/constants/maverick';
import { useExperimentService } from '@/hooks/experimentService';
import { EXPERIMENT_JF_PERSISTENT_CART } from '@/lib/constants/experiments';

export interface UseJoinFlowButtonLogicReturn
  extends UseSharedButtonLogicReturn {
  buttonsVariant: CartButtonVariants;
}

interface UseJoinFlowButtonLogicProps {
  variant: CartButtonVariants;
  skipMemSelectFlow?: string;
  showTrialBlock?: boolean;
}

export function useJoinFlowButtonLogic({
  variant,
  skipMemSelectFlow,
  showTrialBlock = false,
}: UseJoinFlowButtonLogicProps): UseJoinFlowButtonLogicReturn {
  const { cart } = useSiteStore(
    useShallow((state) => ({
      cart: state.cart,
    })),
  );
  const { getExistingVariant } = useExperimentService();
  const experimentVariant = getExistingVariant(EXPERIMENT_JF_PERSISTENT_CART);
  const isPersistentCartExperiment = experimentVariant === 'experiment';
  const page = useGetCurrentPageMaverick();

  // Check if trial membership is in cart
  const membershipInCart = getMembershipInCart(cart.cartItemGroups);
  const hasTrialInCart = membershipInCart
    ? getIsTypeFromAttributes(membershipInCart.attributes, PlanTypes.TRIAL)
    : false;

  // Check what items are in cart
  const hasDeviceInCart = hasProductTypeInCartItemGroups(
    cart.cartItemGroups,
    ProductType.DEVICE,
  );
  const hasPlanInCart = hasProductTypeInCartItemGroups(
    cart.cartItemGroups,
    ProductType.MEMBERSHIP,
  );

  // Determine button variant based on join flow specific logic
  let buttonsVariant = variant ?? 'control';

  // Override logic for specific pages
  if (page === JOIN_FLOW_STEPS.JOIN) {
    if (hasTrialInCart) {
      // Trial in cart: always show two CTAs (Checkout + Add Accessories)
      buttonsVariant = 'checkoutAndAddAccessories';
    } else {
      // Non-trial cart scenarios
      if (hasDeviceInCart) {
        if (hasPlanInCart) {
          // Membership/device/band + plan: two CTAs (Checkout + Add Accessories)
          buttonsVariant = 'checkoutAndAddAccessories';
        } else {
          // Membership/device/band: one CTA (Checkout)
          buttonsVariant = 'continueToMembership';
        }
      }
    }
  }

  if (page === JOIN_FLOW_STEPS.BANDS) {
    buttonsVariant = 'continueToMembership';
    if (hasPlanInCart) {
      if (isPersistentCartExperiment) {
        buttonsVariant = 'checkoutAndAddAccessories';
      } else {
        buttonsVariant =
          skipMemSelectFlow === TRIAL
            ? 'checkoutAndAddAccessories'
            : 'continueToCheckout';
      }
    }
  }

  if (page === JOIN_FLOW_STEPS.MEMBERSHIP) {
    if (hasTrialInCart) {
      buttonsVariant = 'checkoutAndAddAccessories';
    } else {
      buttonsVariant = 'continueToAccessories';
    }
  }
  // Get shared button logic
  const sharedLogic = useSharedButtonLogic({
    isUpgradeFlow: false,
    buttonsVariant,
  });

  return {
    ...sharedLogic,
    buttonsVariant,
  };
}
